import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useEffect,
} from 'react';
import { IntlProvider } from 'react-intl';
import PropTypes from 'prop-types';
import { EN_US, ZH_CN, ZH_TW } from 'shared-constants';
import enUS from '../i18n/en-us';
import zhTW from '../i18n/zh-tw';
import zhCN from '../i18n/zh-cn';

const LocaleContext = createContext<{
  locale: string;
  setLocale: React.Dispatch<React.SetStateAction<string>>;
} | null>(null);

interface LocaleProviderProps {
  messages: { [key: string]: any };
  locale: string;
  children: React.ReactNode;
}

export const LocaleProvider = ({
  messages,
  children,
  locale,
}: LocaleProviderProps) => {
  const [currentLocale, setCurrentLocale] = useState(locale);
  const localeValue = useMemo(
    () => ({ locale: currentLocale, setLocale: setCurrentLocale }),
    [currentLocale],
  );
  useEffect(() => {
    setCurrentLocale(locale);
  }, [locale]);
  return (
    <LocaleContext.Provider value={localeValue}>
      <IntlProvider
        locale={localeValue.locale}
        messages={messages[localeValue.locale]}
      >
        {children}
      </IntlProvider>
    </LocaleContext.Provider>
  );
};

LocaleProvider.propTypes = {
  children: PropTypes.element,
  messages: PropTypes.object,
  locale: PropTypes.string,
};

LocaleProvider.defaultProps = {
  messages: {
    [EN_US]: enUS,
    [ZH_CN]: zhCN,
    [ZH_TW]: zhTW,
  },
};

export const useLocale = () => {
  const context = useContext(LocaleContext);
  if (context === undefined || context === null) {
    throw new Error('useLocale must be used within a LocaleProvider');
  }
  return context;
};
